import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  computed,
  effect,
  inject,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import * as L from 'leaflet';
import { SbcApiService, SystemLocation } from '../services/sbc-api.service';
import { SessionService } from '../../../core/services/session.service';
import {
  BoundaryCoordMode,
  detectBoundaryCoordMode,
  extractGeoJsonRing,
  latLngsToGeoJsonPolygon,
  LatLng,
  normalizeBoundaryGeojson,
  ringToLeafletLatLngs,
} from '../services/geo.util';
import {
  BulkBoundaryPreview,
  parseGeoJsonBoundaryFeatures,
  previewBulkBoundaryImport,
} from '../services/plot-boundary-import.util';
import { PageBackButtonComponent } from '../../../shared/components/page-back-button/page-back-button.component';
import { PageRefreshButtonComponent } from '../../../shared/components/page-refresh-button/page-refresh-button.component';
import { ConfirmDialogComponent } from '../../../shared/components/confirm-dialog/confirm-dialog.component';

type PlotRow = {
  location: SystemLocation;
  hasBoundary: boolean;
};

type ConfirmAction =
  | { kind: 'bulkImport' }
  | { kind: 'removeBoundary' }
  | { kind: 'sectorChange'; sectorId: number | null }
  | { kind: 'plotSwitch'; plotId: number };

@Component({
  selector: 'app-plot-boundaries',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, ConfirmDialogComponent, PageRefreshButtonComponent, PageBackButtonComponent],
  templateUrl: './plot-boundaries.component.html',
  styleUrl: './plot-boundaries.component.css',
})
export class PlotBoundariesComponent implements AfterViewInit, OnDestroy {
  private readonly api = inject(SbcApiService);
  private readonly route = inject(ActivatedRoute);
  readonly session = inject(SessionService);

  private readonly mapHost = viewChild<ElementRef<HTMLElement>>('mapHost');
  private map: L.Map | null = null;
  private baseLayer: L.TileLayer | null = null;
  private layerPlots!: L.LayerGroup;
  private layerDraft!: L.LayerGroup;
  private layerHandles!: L.LayerGroup;

  loading = signal(false);
  saving = signal(false);
  bulkImporting = signal(false);
  error = signal('');
  toast = signal('');
  confirmOpen = signal(false);
  confirmTitle = signal('Confirm');
  confirmMessage = signal('');
  confirmLabel = signal('Yes');
  confirmDestructive = signal(false);
  private pendingConfirm = signal<ConfirmAction | null>(null);
  locations = signal<SystemLocation[]>([]);
  sectorId = signal<number | null>(null);
  selectedPlotId = signal<number | null>(null);
  bulkPreview = signal<BulkBoundaryPreview | null>(null);
  bulkFileName = signal('');
  private bulkGeoJson: Record<string, unknown> | null = null;
  coordMode = signal<BoundaryCoordMode>('wgs84');
  drawing = signal(false);
  draftPoints = signal<L.LatLng[]>([]);
  private boundMapHost: HTMLElement | null = null;
  private readonly defaultMapCenter = L.latLng(25.4358, 81.8463);

  readonly sectors = computed(() =>
    [...this.locations()]
      .filter((l) => l.locationTypeCode === 'SECTOR' && l.statusCd === 'active')
      .sort((a, b) => a.name.localeCompare(b.name))
  );

  readonly sectorPlots = computed((): PlotRow[] => {
    const sectorId = this.sectorId();
    if (sectorId == null) return [];
    return this.locations()
      .filter(
        (l) =>
          l.parentLocationId === sectorId &&
          l.locationTypeCode === 'PLOT' &&
          l.statusCd === 'active'
      )
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((location) => ({
        location,
        hasBoundary: this.hasBoundary(location),
      }));
  });

  readonly boundaryStats = computed(() => {
    const rows = this.sectorPlots();
    const withBoundary = rows.filter((r) => r.hasBoundary).length;
    return { total: rows.length, withBoundary, ready: rows.length > 0 && withBoundary === rows.length };
  });

  readonly bulkPreviewSummary = computed(() => {
    const preview = this.bulkPreview();
    if (!preview) return null;
    return {
      matched: preview.matches.length,
      unmatchedFeatures: preview.unmatchedFeatures.length,
      unmatchedPlots: preview.unmatchedPlots.length,
      duplicates: preview.duplicatePlotIds.length,
    };
  });

  readonly selectedPlot = computed(() => {
    const id = this.selectedPlotId();
    if (id == null) return null;
    return this.sectorPlots().find((r) => r.location.id === id)?.location ?? null;
  });

  readonly statusLine = computed(() => {
    const plot = this.selectedPlot();
    if (!plot) return 'Choose sector and plot from the left panel.';
    if (this.drawing()) return 'Click map corners, then press Done.';
    if (this.draftPoints().length >= 3) return 'Shape ready — press Save boundary below.';
    if (this.hasBoundary(plot)) return 'Boundary saved. Use Edit on map to change, or Import GeoJSON.';
    return 'No boundary yet — Import GeoJSON or Draw on map.';
  });

  constructor() {
    this.route.queryParamMap.subscribe((params) => {
      const raw = params.get('sectorId');
      const plotRaw = params.get('plotId');
      const sectorId = raw ? Number(raw) : null;
      this.sectorId.set(sectorId != null && !Number.isNaN(sectorId) ? sectorId : null);
      const plotId = plotRaw ? Number(plotRaw) : null;
      if (plotId != null && !Number.isNaN(plotId)) {
        this.selectedPlotId.set(plotId);
      }
    });

    effect(() => {
      this.session.authorityId();
      untracked(() => this.reloadLocations());
    });

    effect(() => {
      this.sectorId();
      this.locations();
      if (!this.draftPoints().length) {
        this.inferCoordMode();
      }
      this.refreshMapLayers();
    });

    effect(() => {
      this.selectedPlotId();
      if (!this.draftPoints().length) {
        this.inferCoordMode();
      }
      this.draftPoints();
      this.drawing();
      this.refreshMapLayers();
    });

    // #mapHost lives inside *ngIf (sector selected + loaded) — init after it appears in DOM.
    effect(() => {
      if (this.loading() || this.sectorId() == null) return;
      queueMicrotask(() => this.ensureMapReady());
    });
  }

  ngAfterViewInit(): void {
    this.ensureMapReady();
  }

  ngOnDestroy(): void {
    this.teardownMap();
  }

  reloadLocations(): void {
    this.loading.set(true);
    this.error.set('');
    this.api.listSystemLocations({ includeInactive: 'false' }).subscribe({
      next: (res) => {
        this.locations.set(res.data || []);
        this.loading.set(false);
        if (this.sectorId() == null && this.sectors().length === 1) {
          this.sectorId.set(this.sectors()[0].id);
        }
        if (this.selectedPlotId() == null && this.sectorPlots().length) {
          this.onPlotSelect(this.sectorPlots()[0].location.id, false);
        }
        queueMicrotask(() => {
          if (!this.draftPoints().length) {
            this.inferCoordMode();
          }
          this.ensureMapReady();
        });
      },
      error: (err) => {
        this.loading.set(false);
        this.error.set(err?.error?.message || 'Unable to load locations');
      },
    });
  }

  onSectorChange(raw: string): void {
    const id = raw ? Number(raw) : null;
    const newSectorId = id != null && !Number.isNaN(id) ? id : null;
    if (this.hasUnsavedDraft()) {
      this.openConfirm(
        'Unsaved draft',
        'You have an unsaved boundary draft. Change sector without saving?',
        { kind: 'sectorChange', sectorId: newSectorId }
      );
      return;
    }
    this.applySectorChange(newSectorId);
  }

  private applySectorChange(sectorId: number | null): void {
    this.sectorId.set(sectorId);
    this.selectedPlotId.set(null);
    this.drawing.set(false);
    this.draftPoints.set([]);
    this.clearBulkPreview();
    const first = this.sectorPlots()[0];
    if (first) this.onPlotSelect(first.location.id, false);
  }

  onPlotSelect(raw: number | string | null, confirmUnsaved = true): void {
    if (raw == null || raw === '') {
      this.drawing.set(false);
      this.draftPoints.set([]);
      this.selectedPlotId.set(null);
      return;
    }
    const id = Number(raw);
    if (!Number.isFinite(id) || id <= 0) return;
    if (id === this.selectedPlotId()) return;
    if (confirmUnsaved && this.hasUnsavedDraft()) {
      this.openConfirm(
        'Unsaved changes',
        'Unsaved changes on this plot. Switch without saving?',
        { kind: 'plotSwitch', plotId: id }
      );
      return;
    }
    this.applyPlotSelect(id);
  }

  private applyPlotSelect(id: number): void {
    this.drawing.set(false);
    this.draftPoints.set([]);
    this.selectedPlotId.set(id);
    queueMicrotask(() => this.focusSelectedPlotOnMap());
  }

  plotOptionLabel(row: PlotRow): string {
    const status = row.hasBoundary ? 'Saved' : 'Missing';
    return `${row.location.name} · ${status}`;
  }

  selectPlot(id: number): void {
    this.onPlotSelect(id);
  }

  editOnMap(): void {
    const plot = this.selectedPlot();
    if (!plot) {
      this.showToast('Select a plot first');
      return;
    }
    if (this.hasBoundary(plot)) {
      this.loadPlotBoundaryToDraft(false);
      this.showToast('Edit the shape, then Save boundary');
      return;
    }
    this.startDrawing();
  }

  private hasUnsavedDraft(): boolean {
    return this.draftPoints().length > 0;
  }

  hasBoundary(loc: SystemLocation): boolean {
    const ring = extractGeoJsonRing(loc.boundaryGeojson);
    return !!ring && ring.length >= 3;
  }

  private plotBoundaryGeojson(loc: SystemLocation | null | undefined): Record<string, unknown> | null {
    return normalizeBoundaryGeojson(loc?.boundaryGeojson);
  }

  startDrawing(): void {
    if (!this.selectedPlot()) {
      this.showToast('Select a plot first');
      return;
    }
    this.drawing.set(true);
    this.draftPoints.set([]);
    this.showToast('Click corners on the map, then Done');
  }

  cancelDrawing(): void {
    this.drawing.set(false);
    this.draftPoints.set([]);
  }

  undoPoint(): void {
    const pts = [...this.draftPoints()];
    pts.pop();
    this.draftPoints.set(pts);
  }

  finishDrawing(): void {
    if (this.draftPoints().length < 3) {
      this.showToast('Add at least 3 corners before finishing');
      return;
    }
    this.drawing.set(false);
  }

  clearDraft(): void {
    this.draftPoints.set([]);
    this.drawing.set(false);
  }

  onMapClick(e: L.LeafletMouseEvent): void {
    if (!this.drawing() || !this.selectedPlot()) return;
    this.draftPoints.set([...this.draftPoints(), e.latlng]);
  }

  onGeoJsonFileSelected(event: Event): void {
    const sectorId = this.sectorId();
    if (sectorId == null) {
      this.showToast('Select a sector first');
      return;
    }

    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      try {
        const geo = JSON.parse(String(reader.result || '')) as Record<string, unknown>;
        const features = parseGeoJsonBoundaryFeatures(geo);
        if (!features.length) {
          this.showToast('GeoJSON must contain at least one Polygon');
          input.value = '';
          return;
        }

        if (features.length === 1) {
          this.importSinglePlotGeoJson(features[0].boundaryGeojson);
        } else {
          this.importBulkGeoJson(geo, file.name, features);
        }
      } catch {
        this.showToast('Invalid GeoJSON file');
      }
      input.value = '';
    };
    reader.readAsText(file);
  }

  private importSinglePlotGeoJson(geo: Record<string, unknown>): void {
    const plot = this.selectedPlot();
    if (!plot) {
      this.showToast('Select a plot first for single-shape import');
      return;
    }

    const ring = extractGeoJsonRing(geo);
    if (!ring || ring.length < 3) {
      this.showToast('GeoJSON must be a Polygon with at least 3 points');
      return;
    }

    const mode = detectBoundaryCoordMode(geo);
    this.draftPoints.set(ringToLeafletLatLngs(ring, mode).map((p) => L.latLng(p.lat, p.lng)));
    this.setStorageCoordMode(mode);
    this.drawing.set(false);
    this.clearBulkPreview();
    this.showToast(`GeoJSON loaded — press Save boundary`);
    queueMicrotask(() => this.refreshMapLayers());
  }

  private importBulkGeoJson(
    geo: Record<string, unknown>,
    fileName: string,
    features: ReturnType<typeof parseGeoJsonBoundaryFeatures>
  ): void {
    const preview = previewBulkBoundaryImport(
      features,
      this.sectorPlots().map((row) => row.location)
    );
    this.bulkGeoJson = geo;
    this.bulkPreview.set(preview);
    this.bulkFileName.set(fileName);
    this.draftPoints.set([]);
    this.drawing.set(false);

    if (preview.matches.length) {
      this.onPlotSelect(preview.matches[0].plot.id, false);
    }
    this.inferCoordModeFromBoundaries(features.map((f) => f.boundaryGeojson));
    this.refreshMapLayers();

    this.showToast(`${preview.matches.length} plot(s) matched — press Save all matched`);
  }

  applyBulkImport(): void {
    const sectorId = this.sectorId();
    const preview = this.bulkPreview();
    const geojson = this.bulkGeoJson;
    if (sectorId == null || !preview?.matches.length || !geojson) {
      this.showToast('Upload a FeatureCollection and match at least one plot first');
      return;
    }

    this.openConfirm(
      'Save plot boundaries',
      `Save boundaries for ${preview.matches.length} plot(s) in this sector?`,
      { kind: 'bulkImport' }
    );
  }

  private executeBulkImport(): void {
    const sectorId = this.sectorId();
    const preview = this.bulkPreview();
    const geojson = this.bulkGeoJson;
    if (sectorId == null || !preview?.matches.length || !geojson) return;

    this.bulkImporting.set(true);
    this.api.bulkImportPlotBoundaries({ sectorId, geojson }).subscribe({
      next: (res) => {
        this.bulkImporting.set(false);
        const savedCount = res.meta?.savedCount ?? res.data?.saved?.length ?? preview.matches.length;
        this.clearBulkPreview();
        this.showToast(`Bulk import complete — ${savedCount} plot boundary(ies) saved`);
        this.reloadLocations();
      },
      error: (err) => {
        this.bulkImporting.set(false);
        this.showToast(err?.error?.message || 'Bulk import failed');
      },
    });
  }

  clearBulkPreview(): void {
    this.bulkPreview.set(null);
    this.bulkFileName.set('');
    this.bulkGeoJson = null;
  }

  featurePropertySummary(properties: Record<string, unknown>): string {
    const khasra =
      properties['khasra_number'] ??
      properties['khasra'] ??
      properties['Khasra'] ??
      properties['KHASRA'];
    const code = properties['plot_code'] ?? properties['code'] ?? properties['location_code'];
    const parts: string[] = [];
    if (khasra != null) parts.push(`khasra ${khasra}`);
    if (code != null) parts.push(String(code));
    return parts.length ? parts.join(' · ') : 'No khasra/code property';
  }

  saveBoundary(): void {
    const plot = this.selectedPlot();
    if (!plot) return;

    let points = this.draftPoints();
    if (points.length < 3) {
      const ring = extractGeoJsonRing(plot.boundaryGeojson);
      if (ring && ring.length >= 3) {
        this.showToast('No new drawing — boundary unchanged');
        return;
      }
      this.showToast('Draw a boundary or upload GeoJSON first');
      return;
    }

    const mode = this.coordMode();
    const boundaryGeojson = latLngsToGeoJsonPolygon(
      points.map((p) => ({ lat: p.lat, lng: p.lng })),
      mode
    );

    this.saving.set(true);
    this.api.updateSystemLocation(plot.id, { boundaryGeojson }).subscribe({
      next: (res) => {
        this.saving.set(false);
        this.draftPoints.set([]);
        this.showToast(`Boundary saved for ${plot.name}`);
        const updated = res.data;
        if (updated) {
          this.locations.update((rows) =>
            rows.map((r) => (r.id === updated.id ? { ...r, ...updated } : r))
          );
          this.inferCoordMode();
          this.refreshMapLayers();
        } else {
          this.reloadLocations();
        }
      },
      error: (err) => {
        this.saving.set(false);
        this.showToast(err?.error?.message || 'Save failed');
      },
    });
  }

  removeBoundary(): void {
    const plot = this.selectedPlot();
    if (!plot || !this.hasBoundary(plot)) return;
    this.openConfirm(
      'Remove boundary',
      `Remove boundary for ${plot.name}? LiDAR matching will fail until a new boundary is saved.`,
      { kind: 'removeBoundary' },
      true
    );
  }

  private executeRemoveBoundary(): void {
    const plot = this.selectedPlot();
    if (!plot || !this.hasBoundary(plot)) return;
    this.saving.set(true);
    this.api.updateSystemLocation(plot.id, { boundaryGeojson: null }).subscribe({
      next: () => {
        this.saving.set(false);
        this.draftPoints.set([]);
        this.showToast(`Boundary removed for ${plot.name}`);
        this.reloadLocations();
      },
      error: (err) => {
        this.saving.set(false);
        this.showToast(err?.error?.message || 'Remove failed');
      },
    });
  }

  loadPlotBoundaryToDraft(showErrors = true): void {
    const plot = this.selectedPlot();
    const geo = this.plotBoundaryGeojson(plot);
    if (!plot || !geo) {
      if (showErrors) this.showToast('No saved boundary for this plot');
      return;
    }
    const ring = extractGeoJsonRing(geo);
    if (!ring || ring.length < 3) {
      if (showErrors) this.showToast('Saved boundary is not valid');
      return;
    }
    const mode = detectBoundaryCoordMode(geo);
    const points = ringToLeafletLatLngs(ring, mode).map((p) => L.latLng(p.lat, p.lng));
    this.drawing.set(false);
    this.draftPoints.set(points);
    this.setStorageCoordMode(mode);
    queueMicrotask(() => this.refreshMapLayers());
  }

  private focusSelectedPlotOnMap(): void {
    this.inferCoordMode();
    this.refreshMapLayers();
  }

  private teardownMap(): void {
    this.map?.remove();
    this.map = null;
    this.baseLayer = null;
    this.boundMapHost = null;
  }

  private initLayerGroups(): void {
    this.layerPlots = L.layerGroup();
    this.layerDraft = L.layerGroup();
    this.layerHandles = L.layerGroup();
  }

  private inferCoordMode(): void {
    const plot = this.selectedPlot();
    const plotGeo = this.plotBoundaryGeojson(plot);
    if (plotGeo) {
      this.setStorageCoordMode(detectBoundaryCoordMode(plotGeo));
      return;
    }

    const sectorBoundaries = this.sectorPlots()
      .map((row) => this.plotBoundaryGeojson(row.location))
      .filter((geo): geo is Record<string, unknown> => !!geo);
    if (sectorBoundaries.length) {
      this.inferCoordModeFromBoundaries(sectorBoundaries);
      return;
    }

    this.setStorageCoordMode('wgs84');
  }

  private inferCoordModeFromBoundaries(boundaries: unknown[]): void {
    if (!boundaries.length) {
      this.setStorageCoordMode('wgs84');
      return;
    }
    let wgs84 = 0;
    let local = 0;
    for (const geo of boundaries) {
      if (detectBoundaryCoordMode(geo) === 'wgs84') wgs84++;
      else local++;
    }
    this.setStorageCoordMode(wgs84 >= local ? 'wgs84' : 'local');
  }

  private setStorageCoordMode(mode: BoundaryCoordMode): void {
    this.coordMode.set(mode);
    this.refreshMapLayers();
  }

  private rebuildMap(): void {
    const host = this.mapHost()?.nativeElement;
    if (!host) return;

    this.teardownMap();
    this.boundMapHost = host;
    this.initLayerGroups();

    this.map = L.map(host, {
      crs: L.CRS.EPSG3857,
      minZoom: 10,
      maxZoom: 19,
      zoomControl: true,
    });
    this.baseLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: 19,
    });
    this.baseLayer.addTo(this.map);

    this.layerPlots.addTo(this.map);
    this.layerDraft.addTo(this.map);
    this.layerHandles.addTo(this.map);
    this.map.on('click', (e) => this.onMapClick(e));
    this.map.setView(this.defaultMapCenter, 14);
    this.map.invalidateSize();
    queueMicrotask(() => {
      this.map?.invalidateSize();
      this.refreshMapLayers();
    });
  }

  private ensureMapReady(): void {
    const host = this.mapHost()?.nativeElement;
    if (!host) return;

    if (this.map && this.boundMapHost !== host) {
      this.teardownMap();
    }

    if (!this.map) {
      this.boundMapHost = host;
      this.rebuildMap();
      return;
    }

    this.map.invalidateSize();
    this.refreshMapLayers();
  }

  private applyMapCrs(_mode: BoundaryCoordMode): void {
    this.refreshMapLayers();
  }

  private refreshMapLayers(): void {
    if (!this.map) return;

    this.layerPlots.clearLayers();
    this.layerDraft.clearLayers();
    this.layerHandles.clearLayers();

    const rings: number[][][] = [];
    const ringModes: BoundaryCoordMode[] = [];
    const selectedId = this.selectedPlotId();

    for (const row of this.sectorPlots()) {
      const geo = this.plotBoundaryGeojson(row.location);
      const ring = extractGeoJsonRing(geo);
      if (!ring || ring.length < 3) continue;
      const plotMode = detectBoundaryCoordMode(geo);
      rings.push(ring);
      ringModes.push(plotMode);
      const latlngs = ringToLeafletLatLngs(ring, plotMode);
      const isSelected = row.location.id === selectedId;
      L.polygon(latlngs, {
        color: isSelected ? '#0d6e6a' : '#64748b',
        weight: isSelected ? 2.5 : 1.5,
        fillOpacity: isSelected ? 0.35 : 0.18,
        fillColor: isSelected ? '#0d9488' : '#94a3b8',
      })
        .bindTooltip(row.location.name, { sticky: true })
        .addTo(this.layerPlots);
    }

    const draft = this.draftPoints();
    if (draft.length >= 2) {
      L.polyline(draft, { color: '#b45309', weight: 2, dashArray: '4 4' }).addTo(this.layerDraft);
    }
    if (draft.length >= 3 && !this.drawing()) {
      L.polygon(draft, {
        color: '#b45309',
        weight: 2,
        fillOpacity: 0.2,
        fillColor: '#f59e0b',
      }).addTo(this.layerDraft);
    }
    for (const p of draft) {
      L.circleMarker(p, { radius: 6, color: '#b45309', fillColor: '#fff', fillOpacity: 1, weight: 2 }).addTo(
        this.layerHandles
      );
    }

    if (draft.length >= 2) {
      this.map.fitBounds(L.latLngBounds(draft), { padding: [32, 32] });
      this.map.invalidateSize();
      return;
    }

    if (rings.length) {
      const boundsPts: LatLng[] = [];
      for (let i = 0; i < rings.length; i++) {
        boundsPts.push(...ringToLeafletLatLngs(rings[i], ringModes[i]));
      }
      if (boundsPts.length >= 2) {
        this.map.fitBounds(L.latLngBounds(boundsPts), { padding: [24, 24] });
        this.map.invalidateSize();
        return;
      }
    }

    const selectedPlot = this.selectedPlot();
    const selectedGeo = this.plotBoundaryGeojson(selectedPlot);
    const selectedRing = extractGeoJsonRing(selectedGeo);
    if (selectedRing && selectedRing.length >= 3) {
      const selectedMode = detectBoundaryCoordMode(selectedGeo);
      const selectedPts = ringToLeafletLatLngs(selectedRing, selectedMode);
      if (selectedPts.length >= 3) {
        this.map.fitBounds(L.latLngBounds(selectedPts), { padding: [24, 24] });
        this.map.invalidateSize();
        return;
      }
    }

    this.map.setView(this.defaultMapCenter, 14);
    this.map.invalidateSize();
  }

  private showToast(msg: string): void {
    this.toast.set(msg);
    setTimeout(() => this.toast.set(''), 3500);
  }

  private openConfirm(
    title: string,
    message: string,
    action: ConfirmAction,
    destructive = false
  ): void {
    this.confirmTitle.set(title);
    this.confirmMessage.set(message);
    this.confirmLabel.set('Yes');
    this.confirmDestructive.set(destructive);
    this.pendingConfirm.set(action);
    this.confirmOpen.set(true);
  }

  onConfirmDialogConfirm(): void {
    const action = this.pendingConfirm();
    this.confirmOpen.set(false);
    this.pendingConfirm.set(null);
    if (!action) return;

    switch (action.kind) {
      case 'bulkImport':
        this.executeBulkImport();
        break;
      case 'removeBoundary':
        this.executeRemoveBoundary();
        break;
      case 'sectorChange':
        this.applySectorChange(action.sectorId);
        break;
      case 'plotSwitch':
        this.applyPlotSelect(action.plotId);
        break;
    }
  }

  onConfirmDialogCancel(): void {
    this.confirmOpen.set(false);
    this.pendingConfirm.set(null);
  }
}
